import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, ReceiptLong as RecipeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import menuConsumptionRecipeService from '../../services/menuConsumptionRecipeService';
import type { MenuConsumptionRecipe } from '../../types/menu';

/**
 * Phase 4.J — embedded recipes surface for the Menu Detail page.
 *
 * Recipes are venue-scoped storage rows that can be linked from any menu
 * item's per-format pricing row (`MenuItemFormat.consumptionRecipeId`). This
 * panel reuses `menuConsumptionRecipeService` directly rather than
 * re-rendering the full standalone MenuConsumptionRecipeList page, so the
 * layout reads as a tab inside Menus rather than a sub-page.
 *
 * Edit and create actions still navigate to the standalone recipe routes
 * (`/admin/venues/:venueId/menu-consumption-recipes/...`) — those remain
 * authoritative and operators with a deep-link still land where they expect.
 *
 * Optional `linksByRecipeId` lets the parent (MenuDetail) inject a map of
 * recipe._id → number of links from the current menu, derived from the
 * admin render endpoint that MenuDetail already loads for Part C. The map
 * is consulted defensively — when absent, the link column simply omits the
 * count rather than implying zero.
 *
 * Inactive-recipe contract is preserved: the banner explicitly states that
 * linked recipes are not active for sales or stock yet.
 */

interface Props {
  venueId: string;
  /**
   * Optional map of recipe._id → number of serving-size rows on the current
   * menu linking to that recipe. Built by MenuDetail from the render
   * endpoint. Used to highlight linked vs unlinked recipes and drive the
   * operational summary at the top of the panel.
   */
  linksByRecipeId?: Map<string, number>;
  /**
   * Phase 4.M — optional count of distinct COCKTAIL items on this menu that
   * carry at least one recipe link. Derived cheaply in MenuDetail from
   * already-loaded render data; the panel renders it in the summary when
   * present.
   */
  cocktailItemsUsingRecipes?: number;
}

// Phase 4.L — operator-facing language. Avoids "storage only" / "depletion"
// jargon while still honestly stating the recipes aren't active yet.
const INERT_BANNER =
  'Recipes saved here can be reused across menus. They are not active for sales or stock yet.';

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const MenuRecipesPanel: React.FC<Props> = ({ venueId, linksByRecipeId, cocktailItemsUsingRecipes }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const recipeBase = `/admin/venues/${venueId}/menu-consumption-recipes`;

  const [recipes, setRecipes] = useState<MenuConsumptionRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Active-only by default. The standalone page exposes a filter for inactive
  // recipes; this embedded panel keeps the surface compact and assumes the
  // operator wants live recipes only. Inactive recipes remain visible on the
  // standalone /menu-consumption-recipes route.
  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const result = await menuConsumptionRecipeService.getRecipes(venueId, 1, 100, 'true');
      setRecipes(result.data);
    } catch {
      setFetchError('Could not load recipes. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  // Phase 4.M — operational summary derived from already-loaded data.
  // Recipes are counted by their `active` state; "linked" recipes are those
  // appearing in linksByRecipeId with a non-zero count. No backend calls.
  const totalRecipes = recipes.length;
  const linkedRecipeCount = linksByRecipeId
    ? recipes.filter(r => (linksByRecipeId.get(r._id) ?? 0) > 0).length
    : 0;
  const unlinkedRecipeCount = linksByRecipeId
    ? totalRecipes - linkedRecipeCount
    : 0;
  const showSummary = !loading && totalRecipes > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
        {INERT_BANNER}
      </Alert>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RecipeIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.25rem' }} />
            <Typography variant="subtitle1" fontWeight={600}>Recipes used by cocktail serving sizes</Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${recipeBase}/create`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Recipe
          </Button>
        </Box>

        {/* Phase 4.M — operational summary. Shows total / linked / unlinked
            recipes plus how many cocktail items on this menu use a recipe.
            Hidden while loading or when there are no recipes (the empty
            state below already communicates the situation). */}
        {showSummary && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total recipes
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>{totalRecipes}</Typography>
            </Box>
            {linksByRecipeId && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Linked on this menu
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700} color={linkedRecipeCount > 0 ? 'primary.main' : 'text.primary'}>
                    {linkedRecipeCount}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Unlinked
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    {unlinkedRecipeCount}
                  </Typography>
                </Box>
              </>
            )}
            {cocktailItemsUsingRecipes !== undefined && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Cocktails using recipes
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>{cocktailItemsUsingRecipes}</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', maxWidth: 320 }}>
              Recipes are attached from cocktail serving sizes.
            </Typography>
          </Box>
        )}

        {fetchError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setFetchError('')} sx={{ borderRadius: 1 }}>{fetchError}</Alert>
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }} align="right">Components</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }} align="right">Linked on this menu</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : recipes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <RecipeIcon sx={{ fontSize: 36, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        No recipes linked to this menu yet.
                      </Typography>
                      {/* Phase 4.N — explain the relationship + how a recipe
                          shows up on a menu after creation. */}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 420, mx: 'auto', mb: 1.5 }}>
                        Recipes become available when linked from cocktail serving sizes. Create one here, then link it from a cocktail item's serving size.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`${recipeBase}/create`)}
                        sx={{ mt: 1, textTransform: 'none' }}
                      >
                        Create first recipe
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                recipes.map(recipe => {
                  const linkedOnThisMenu = linksByRecipeId?.get(recipe._id);
                  return (
                    <TableRow
                      key={recipe._id}
                      hover
                      onClick={() => navigate(`${recipeBase}/edit/${recipe._id}`)}
                      sx={{ cursor: 'pointer', opacity: recipe.active ? 1 : 0.55 }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{recipe.name}</Typography>
                        {recipe.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {recipe.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {recipe.components?.length ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {/* Only render a value when the parent supplied the map.
                            Absence !== zero — without the map we can't claim
                            either, so we display an em-dash placeholder. */}
                        {linksByRecipeId
                          ? (linkedOnThisMenu && linkedOnThisMenu > 0 ? (
                              <Chip
                                label={`${linkedOnThisMenu} link${linkedOnThisMenu === 1 ? '' : 's'}`}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20, backgroundColor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 600 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">0</Typography>
                            ))
                          : (<Typography variant="caption" color="text.disabled">—</Typography>)
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={recipe.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={recipe.active ? 'success' : 'default'}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Edit recipe">
                          <IconButton size="small" onClick={() => navigate(`${recipeBase}/edit/${recipe._id}`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Recipes are reusable across menus, so editing one updates every menu item that links to it.
        Archiving recipes is available on the standalone recipes page.
      </Typography>
    </Box>
  );
};

export default MenuRecipesPanel;
